import { Box, Container, Typography, Avatar, Card, CardContent, Grid, Stack, Divider, Paper } from '@mui/material';
import {
  LocationOnOutlined as LocationOnOutlinedIcon,
  LanguageOutlined as LanguageOutlinedIcon,
  MailOutlineOutlined as MailOutlineIcon,
  FiberManualRecord as DotIcon,
  TrendingUp as TrendingUpIcon,
  AutoAwesome as SparkleIcon,
  ImageOutlined as ImageOutlinedIcon,
  Person as PersonOutlineIcon,
} from '@mui/icons-material';
import { formatNumber } from '../../lib/format';
import { getExampleCreatorUsername } from '../../lib/analytics';
import { countryFlag, platformHomeUrl, platformIconData, socialProfileUrl } from '../../lib/social';
import { navigate } from '../../lib/navigation';
import Badge from '../../components/Badge';
import Button from '../../components/Button';
import FloatingHelpButton from '../../components/FloatingHelpButton';
import InviteBanner from '../../components/InviteBanner';
import Navbar from '../../components/Navbar';
import CollapsibleProfileSection from './CollapsibleProfileSection';
import { exampleProfileState } from './exampleProfileData';
import ProfileSection from './ProfileSection';
import PublicCollabCard from './PublicCollabCard';
import PublicPortfolioCard from './PublicPortfolioCard';
import PublicSocialCard from './PublicSocialCard';
import { opportunityPreferenceLabel } from '../../lib/opportunityPreferences';

function MetaItem({ icon, label, children }) {
  return (
    <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
      {icon}
      <Typography variant="body2" color="text.secondary" component="span">
        {label}
      </Typography>
      {children}
    </Stack>
  );
}

function PreviewPrompt({ text, path }) {
  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between' }}>
      <Typography color="text.secondary">{text}</Typography>
      <Button variant="secondary" onClick={() => navigate(path)}>Add details</Button>
    </Stack>
  );
}

function PublicProfilePage({ auth, previewState, publicState }) {
  const authed = auth?.status === 'authenticated';
  const isPreview = Boolean(previewState);
  const isRealProfile = Boolean(publicState);
  const state = publicState || previewState || exampleProfileState;
  const isExample = !isRealProfile && !isPreview;
  const isInvite = isExample && Boolean(getExampleCreatorUsername());
  const displayName = state.creator.displayName.trim() || 'this creator';
  const publicCollaborations = state.collaborations.filter((collab) => collab.visibility === 'Public');
  const publicPortfolio = state.portfolio.filter((item) => item.visibility === 'Public');
  const totalAudience = state.socials.reduce((sum, social) => sum + social.followers, 0);
  const opportunityPreferences = state.creator.opportunityPreferences || [];

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      <Navbar authed={authed} />

      {isInvite && <InviteBanner />}

      {!isRealProfile && (
        <Box
          sx={{
            bgcolor: 'rgba(78, 40, 181, 0.06)',
            borderBottom: '1px solid rgba(78, 40, 181, 0.12)',
          }}
        >
          <Container maxWidth="lg">
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={{ xs: 1, sm: 2 }}
              sx={{ py: 1.25, alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between' }}
            >
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <SparkleIcon sx={{ color: 'primary.main', fontSize: 18 }} />
                <Typography variant="body2" sx={{ color: 'primary.dark', fontWeight: 600 }}>
                  {isPreview ? 'This is a preview of your public CreatorCV profile.' : 'This is an example CreatorCV profile.'}
                </Typography>
              </Stack>
              <Button variant="secondary" onClick={() => navigate(isPreview ? '/dashboard' : authed ? '/dashboard' : '/signup')}>
                {isPreview ? 'Back to dashboard' : 'Create yours now'}
              </Button>
            </Stack>
          </Container>
        </Box>
      )}

      {state.creator.coverUrl ? (
        <Box
          component="img"
          src={state.creator.coverUrl}
          alt="Cover"
          sx={{ width: '100%', height: { xs: 160, sm: 240 }, objectFit: 'cover', display: 'block' }}
        />
      ) : (
        <Box
          sx={{
            width: '100%',
            height: { xs: 160, sm: 240 },
            bgcolor: 'grey.200',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <ImageOutlinedIcon sx={{ color: 'grey.400', fontSize: 40 }} />
        </Box>
      )}

      <Container maxWidth="lg">
        <Paper
          sx={{
            position: 'relative',
            mt: { xs: -4, sm: -6 },
            mb: 3,
            p: { xs: 2.5, sm: 3.5 },
            borderRadius: 4,
            boxShadow: '0 8px 28px rgba(20, 10, 50, 0.1)',
          }}
        >
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} sx={{ alignItems: { xs: 'flex-start', sm: 'flex-end' }, mb: 2 }}>
            <Avatar
              src={state.creator.avatarUrl || undefined}
              sx={{
                width: { xs: 96, sm: 132 },
                height: { xs: 96, sm: 132 },
                border: '4px solid white',
                boxShadow: '0 2px 10px rgba(20, 10, 50, 0.15)',
                flexShrink: 0,
                mt: { xs: -6, sm: -9 },
                bgcolor: 'grey.200',
              }}
            >
              {!state.creator.avatarUrl && <PersonOutlineIcon sx={{ color: 'grey.500', fontSize: { xs: 40, sm: 56 } }} />}
            </Avatar>
            <Box
              sx={{
                minWidth: 0,
                flexGrow: 1,
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: { xs: 'flex-start', sm: 'center' },
                columnGap: 8,
                rowGap: 1.5,
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: { xs: '1.75rem', sm: '2.25rem' },
                    fontWeight: 700,
                    lineHeight: 1.2,
                    color: state.creator.displayName ? 'text.primary' : 'text.disabled',
                    fontStyle: state.creator.displayName ? 'normal' : 'italic',
                  }}
                >
                  {state.creator.displayName || 'Unnamed creator'}
                </Typography>
                <Typography
                  sx={{
                    color: state.creator.username ? 'text.secondary' : 'text.disabled',
                    fontStyle: state.creator.username ? 'normal' : 'italic',
                  }}
                >
                  {state.creator.username ? `@${state.creator.username}` : 'No username set'}
                </Typography>
                {state.socials.length > 0 && (
                  <Stack spacing={0.75} sx={{ mt: 1, minWidth: 0 }}>
                    {state.socials.map((social) => {
                      const href = isExample ? platformHomeUrl(social.platform) : socialProfileUrl(social);
                      return (
                        <Stack
                          key={social.id}
                          component={href ? 'a' : 'div'}
                          href={href || undefined}
                          target={href ? '_blank' : undefined}
                          rel={href ? 'noreferrer' : undefined}
                          direction="row"
                          spacing={1}
                          sx={{
                            alignItems: 'center',
                            color: 'text.secondary',
                            textDecoration: 'none',
                            width: 'fit-content',
                            ...(href && { '&:hover': { color: 'primary.main' } }),
                          }}
                        >
                          <Box
                            component="img"
                            src={platformIconData(social.platform)}
                            alt=""
                            sx={{ width: 18, height: 18, borderRadius: '4px', flexShrink: 0 }}
                          />
                          <Typography variant="body2" color="inherit" noWrap>
                            {social.handle || social.platform}
                          </Typography>
                        </Stack>
                      );
                    })}
                  </Stack>
                )}
              </Box>
              <Stack
                direction="row"
                spacing={1.25}
                sx={{
                  alignItems: 'flex-start',
                  minWidth: { xs: '100%', sm: 320 },
                  flexGrow: 1,
                }}
              >
                <Box
                  sx={{
                    width: 34,
                    height: 34,
                    borderRadius: '50%',
                    border: '1.5px solid',
                    borderColor: 'primary.main',
                    color: 'primary.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    mt: 0.25,
                  }}
                >
                  <MailOutlineIcon sx={{ fontSize: 17 }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, overflowWrap: 'anywhere' }}>
                    Work with {displayName}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      color: 'text.secondary',
                      whiteSpace: 'pre-line',
                      fontStyle: state.creator.contactInfo ? 'normal' : 'italic',
                    }}
                  >
                    {state.creator.contactInfo || `${displayName} hasn't shared how to get in touch yet.`}
                  </Typography>
                </Box>
              </Stack>
            </Box>
          </Stack>

          <Typography
            variant="h5"
            sx={{
              mb: 2,
              whiteSpace: 'pre-line',
              color: state.creator.headline ? 'text.primary' : 'text.disabled',
              fontStyle: state.creator.headline ? 'normal' : 'italic',
            }}
          >
            {state.creator.headline || 'No professional headline added yet'}
          </Typography>
          <Stack direction="row" spacing={1} useFlexGap sx={{ mb: 2.5, flexWrap: 'wrap' }}>
            {state.creator.niches.map((tag) => (
              <Badge key={tag}>{tag}</Badge>
            ))}
          </Stack>

          <Divider sx={{ mb: 2 }} />

          <Stack direction="row" spacing={{ xs: 2, sm: 4 }} useFlexGap sx={{ flexWrap: 'wrap' }}>
            {state.creator.location && (
              <MetaItem icon={<LocationOnOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />} label="Based in">
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {countryFlag(state.creator.location)} {state.creator.location}
                </Typography>
              </MetaItem>
            )}
            {state.creator.languages.length > 0 && (
              <MetaItem icon={<LanguageOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />} label="Speaks">
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {state.creator.languages.join(', ')}
                </Typography>
              </MetaItem>
            )}
            {state.creator.availability && (
              <MetaItem icon={<DotIcon sx={{ fontSize: 12, color: 'success.main' }} />} label={state.creator.availability} />
            )}
          </Stack>
        </Paper>

        {state.socials.length > 0 && (
          <Card sx={{ borderRadius: 3, boxShadow: '0 1px 3px rgba(20, 10, 50, 0.06)', mb: 3 }}>
            <CardContent sx={{ p: { xs: 2.5, sm: 3.5 } }}>
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 0.5 }}>
                <TrendingUpIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                <Typography variant="overline" color="text.secondary">
                  Total audience
                </Typography>
              </Stack>
              <Typography variant="h3" sx={{ mb: 2.5 }}>
                {formatNumber(totalAudience)}
              </Typography>
              <Grid container spacing={2}>
                {state.socials.map((social) => (
                  <Grid size={{ xs: 12, sm: 4 }} key={social.id}>
                    <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
                      <Avatar src={platformIconData(social.platform)} variant="rounded" />
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          {social.platform}
                        </Typography>
                        <Typography fontWeight={700}>{formatNumber(social.followers)}</Typography>
                      </Box>
                    </Stack>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        )}

        <Stack spacing={2} sx={{ mb: 6 }}>
          <ProfileSection
            title={`About ${displayName}`}
            action={isPreview && (!state.creator.bio || (!state.creator.creatorTypes.length && !state.creator.niches.length))
              ? <Button variant="ghost" onClick={() => navigate('/dashboard/profile')}>Edit profile</Button>
              : null}
          >
            <Typography
              sx={{
                mb: 2,
                whiteSpace: 'pre-line',
                color: state.creator.bio ? 'text.primary' : 'text.disabled',
                fontStyle: state.creator.bio ? 'normal' : 'italic',
              }}
            >
              {state.creator.bio || 'No bio added yet.'}
            </Typography>
            <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
              {[...state.creator.creatorTypes, ...state.creator.niches].map((tag) => (
                <Badge key={tag}>{tag}</Badge>
              ))}
            </Stack>
          </ProfileSection>

          {opportunityPreferences.length > 0 && (
            <ProfileSection title="Open to">
              <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
                {opportunityPreferences.map((preference) => (
                  <Badge key={preference} tone="info">{opportunityPreferenceLabel(preference)}</Badge>
                ))}
              </Stack>
            </ProfileSection>
          )}

          {publicPortfolio.length > 0 && (
            <ProfileSection title="Content">
              <Grid container spacing={2}>
                {publicPortfolio.slice(0, 8).map((item) => (
                  <Grid size={{ xs: 12, sm: 6, md: 3 }} key={item.id}>
                    <PublicPortfolioCard item={item} />
                  </Grid>
                ))}
              </Grid>
            </ProfileSection>
          )}

          {isPreview && publicPortfolio.length === 0 && (
            <ProfileSection title="Content">
              <PreviewPrompt text="Add content samples to show brands the quality and style of your work." path="/dashboard/portfolio" />
            </ProfileSection>
          )}

          {state.socials.length > 0 && (
            <CollapsibleProfileSection title="Social presence" summary={`${state.socials.length} platforms`} defaultOpen>
              <Grid container spacing={2}>
                {state.socials.map((social) => (
                  <Grid size={{ xs: 12, sm: 6, md: 4 }} key={social.id}>
                    <PublicSocialCard social={social} />
                  </Grid>
                ))}
              </Grid>
            </CollapsibleProfileSection>
          )}

          {isPreview && state.socials.length === 0 && (
            <ProfileSection title="Social presence">
              <PreviewPrompt text="Add your platforms and audience stats so brands can understand your reach." path="/dashboard/socials" />
            </ProfileSection>
          )}

          {publicCollaborations.length > 0 && (
            <CollapsibleProfileSection
              title="Collaboration history"
              summary={`${publicCollaborations.length} public collaborations`}
              defaultOpen
            >
              <Stack spacing={2}>
                {publicCollaborations.slice(0, 5).map((collab) => (
                  <PublicCollabCard key={collab.id} collab={collab} />
                ))}
              </Stack>
            </CollapsibleProfileSection>
          )}

          {isPreview && publicCollaborations.length === 0 && (
            <ProfileSection title="Collaboration history">
              <PreviewPrompt text="Add previous brand work to build credibility with future partners." path="/dashboard/collaborations" />
            </ProfileSection>
          )}
        </Stack>
      </Container>
      {isExample && <FloatingHelpButton />}
    </Box>
  );
}

export default PublicProfilePage;
